import React from 'react';
import styled from 'styled-components';

const ChipOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  border-radius: 0.5rem;
  border: 1.5px solid ${(props) => (props.$active ? '#10b981' : '#e5e7eb')};
  background-color: ${(props) => (props.$active ? '#ecfdf5' : '#ffffff')};
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.9rem;
  font-weight: 500;
  color: ${(props) => (props.$active ? '#065f46' : '#4b5563')};

  &:hover {
    border-color: #10b981;
    background-color: #f0fdf4;
  }

  input {
    accent-color: #10b981;
    width: 1rem;
    height: 1rem;
  }
`;

const AttributeHint = styled.p`
  font-size: 0.8rem;
  color: #64748b;
  margin: 0 0 0.75rem 0;
  line-height: 1.45;
`;

const SizeGroupBlock = styled.div`
  margin-bottom: 1rem;
`;

const SizeGroupLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  margin-bottom: 0.5rem;
`;

const ChipGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const ErrorText = styled.span`
  color: #dc3545;
  font-size: 0.875rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0;
`;

const Label = styled.label`
  font-weight: 600;
  color: #1e293b;
  font-size: 0.875rem;
`;

const ColorSelect = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background: #fff;
  color: #1f2937;
  font-size: 0.95rem;
`;

/**
 * Shared size/color chip pickers for create/offer listing forms.
 */
export default function ListingSizeColorFields({
  attrs,
  category,
  color,
  selectedSizes,
  onColorChange,
  onToggleSize,
  errors = {},
  namePrefix = 'listing',
}) {
  if (!attrs) return null;

  return (
    <>
      <FormGroup>
        <Label>Color *</Label>
        <AttributeHint>Choose the closest match from 100+ colors for {category}.</AttributeHint>
        <ColorSelect
          name={`${namePrefix}-color`}
          value={color || ''}
          onChange={(e) => onColorChange(e.target.value)}
        >
          <option value="">Select a color</option>
          {attrs.colors.map((colorOption) => (
            <option key={colorOption} value={colorOption}>{colorOption}</option>
          ))}
          {color && !attrs.colors.includes(color) ? (
            <option value={color}>{color} (current value)</option>
          ) : null}
        </ColorSelect>
        {errors.color && <ErrorText>{errors.color}</ErrorText>}
      </FormGroup>

      {attrs.requiresSize && attrs.sizeProfile && (
        <FormGroup>
          <Label>{attrs.sizeProfile.label}(s) * — select one or more</Label>
          <AttributeHint>{attrs.sizeProfile.hint}</AttributeHint>
          {attrs.sizeProfile.groups.map((group) => (
            <SizeGroupBlock key={group.label || 'default'}>
              {group.label ? <SizeGroupLabel>{group.label}</SizeGroupLabel> : null}
              <ChipGrid>
                {group.sizes.map((sizeOption) => (
                  <ChipOption
                    key={`${group.label}-${sizeOption}`}
                    $active={selectedSizes.includes(sizeOption)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedSizes.includes(sizeOption)}
                      onChange={() => onToggleSize(sizeOption)}
                    />
                    {sizeOption}
                  </ChipOption>
                ))}
              </ChipGrid>
            </SizeGroupBlock>
          ))}
          {errors.sizes && <ErrorText>{errors.sizes}</ErrorText>}
        </FormGroup>
      )}
    </>
  );
}
